import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { FileText, Truck, Package, CheckCircle, Clock, ExternalLink, Mail, MapPin, Printer, Send, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { t, getLocale } from '@/i18n';

const statusColors = {
  'pending': 'bg-gray-100 text-gray-700',
  'confirmed': 'bg-blue-100 text-blue-700',
  'in-production': 'bg-yellow-100 text-yellow-700',
  'produced': 'bg-purple-100 text-purple-700',
  'dispatched': 'bg-green-100 text-green-700',
  'delivered': 'bg-emerald-100 text-emerald-700',
};

const statusLabels = {
  'pending': t('orders.pending'), 'confirmed': t('orders.confirmed'), 'in-production': t('orders.inProduction'),
  'produced': t('orders.produced'), 'dispatched': t('orders.dispatched'), 'delivered': t('orders.delivered'),
};

const allStatuses = ['pending', 'confirmed', 'in-production', 'produced', 'dispatched', 'delivered'];

const certStatusColors = {
  issued: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
};

function formatDate(ts) {
  if (!ts) return '—';
  const loc = getLocale();
  return new Date(ts).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  const loc = getLocale();
  return `${d.toLocaleDateString(loc, { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

export const OrderDetailSheet = ({ order, onClose, onNavigateToTruck, linkedTrucks = [] }) => {
  if (!order) return null;

  const currentStepIdx = allStatuses.indexOf(order.status);

  return (
    <Sheet open={!!order} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-xl overflow-y-auto p-0">
        {/* Status gradient banner */}
        <div className={cn(
          'px-6 pt-6 pb-4',
          order.status === 'pending' && 'bg-gradient-to-r from-gray-50 to-transparent',
          order.status === 'confirmed' && 'bg-gradient-to-r from-blue-50 to-transparent',
          order.status === 'in-production' && 'bg-gradient-to-r from-yellow-50 to-transparent',
          order.status === 'produced' && 'bg-gradient-to-r from-purple-50 to-transparent',
          order.status === 'dispatched' && 'bg-gradient-to-r from-green-50 to-transparent',
          order.status === 'delivered' && 'bg-gradient-to-r from-emerald-50 to-transparent',
        )}>
          <SheetHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <SheetTitle className="text-xl">{order.id}</SheetTitle>
                <Badge className={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
              </div>
            </div>
          </SheetHeader>

          {/* Action buttons */}
          <div className="flex gap-2 mt-4">
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => toast.success(t('orders.actionSimulated'))}>
              <CheckCheck className="h-3 w-3" /> {t('orders.confirm')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => toast.success(t('orders.actionSimulated'))}>
              <Send className="h-3 w-3" /> {t('orders.dispatch')}
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={() => toast.success(t('orders.actionSimulated'))}>
              <Printer className="h-3 w-3" /> {t('orders.invoice')}
            </Button>
          </div>

          {/* Lifecycle stepper */}
          <div className="flex items-center gap-0.5 mt-4">
            {allStatuses.map((s, i) => (
              <div key={s} className={cn(
                'flex-1 h-1.5 rounded-full',
                i <= currentStepIdx ? 'bg-primary' : 'bg-gray-200',
              )} />
            ))}
          </div>
        </div>

        <Tabs defaultValue="overview" className="px-6 pb-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">{t('orders.tab.overview')}</TabsTrigger>
            <TabsTrigger value="items">{t('orders.tab.items')}</TabsTrigger>
            <TabsTrigger value="timeline">{t('orders.tab.timeline')}</TabsTrigger>
            <TabsTrigger value="delivery">{t('orders.tab.delivery')}</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            {/* Customer card */}
            <div className="bg-muted/50 rounded-xl p-4 space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase">{t('orders.customer')}</div>
              <div className="font-semibold text-lg">{order.customer?.name}</div>
              {order.customer?.contact && (
                <div className="text-sm text-muted-foreground">{order.customer.contact}</div>
              )}
              {order.customer?.email && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Mail className="h-3 w-3" /> {order.customer.email}
                </div>
              )}
              {order.customer?.address && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-3 w-3" /> {order.customer.address}
                </div>
              )}
              {order.customer?.ico && (
                <div className="text-xs text-muted-foreground">{t('orders.ico')}: {order.customer.ico}</div>
              )}
            </div>

            {/* Financial */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <div className="text-xs text-muted-foreground">{t('orders.totalValue')}</div>
                <div className="text-2xl font-extrabold">{order.totalValue?.toLocaleString()} EUR</div>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <div className="text-xs text-muted-foreground">{t('orders.delivery')}</div>
                <div className="text-sm font-bold">{formatDate(order.requestedDelivery)}</div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-muted/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('orders.createdAt')}</span>
                <span className="font-medium">{formatDateTime(order.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('orders.confirmedAt')}</span>
                <span className="font-medium">{formatDateTime(order.confirmedAt)}</span>
              </div>
            </div>
          </TabsContent>

          {/* Items */}
          <TabsContent value="items" className="mt-4 space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('orders.material')}</TableHead>
                  <TableHead>{t('orders.quantity')}</TableHead>
                  <TableHead>{t('orders.pricePerTon')}</TableHead>
                  <TableHead className="text-right">{t('orders.total')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(order.items || []).map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="font-medium">{item.material}</div>
                      <div className="text-xs text-muted-foreground">{item.grade}</div>
                    </TableCell>
                    <TableCell>{item.quantity} {item.unit}</TableCell>
                    <TableCell>{item.pricePerTon} EUR</TableCell>
                    <TableCell className="text-right font-medium">
                      {(item.quantity * item.pricePerTon).toLocaleString()} EUR
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Production progress per item */}
            <div className="space-y-3">
              <div className="text-xs font-semibold text-muted-foreground uppercase">{t('orders.productionProgress')}</div>
              {(order.items || []).map((item, i) => {
                const pct = item.quantity > 0 ? Math.round((item.produced / item.quantity) * 100) : 0;
                return (
                  <div key={i} className="bg-muted/50 rounded-lg p-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{item.material}</span>
                      <span className="text-muted-foreground">{item.produced}/{item.quantity} {item.unit}</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Timeline */}
          <TabsContent value="timeline" className="mt-4">
            {/* Status stepper */}
            <div className="flex items-center gap-1 mb-6">
              {allStatuses.map((s, i) => (
                <div key={s} className="flex-1 flex flex-col items-center gap-1">
                  <div className={cn(
                    'w-full h-2 rounded-full',
                    i <= currentStepIdx ? 'bg-primary' : 'bg-gray-200',
                  )} />
                  <span className={cn('text-[8px]', i <= currentStepIdx ? 'text-primary font-semibold' : 'text-muted-foreground')}>
                    {statusLabels[s]}
                  </span>
                </div>
              ))}
            </div>

            {/* Event list */}
            <div className="space-y-0 relative">
              <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-border" />
              {(order.timeline || []).map((ev, i) => {
                const isLast = i === order.timeline.length - 1;
                return (
                  <div key={i} className="flex gap-3 relative pb-4">
                    <div className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10',
                      isLast ? 'bg-primary text-white' : 'bg-muted',
                    )}>
                      {isLast ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[ev.status]} variant="outline">{statusLabels[ev.status]}</Badge>
                      </div>
                      <div className="text-sm mt-1">{t(ev.note)}</div>
                      <div className="text-xs text-muted-foreground">{formatDateTime(ev.timestamp)}</div>
                    </div>
                  </div>
                );
              })}

              {/* Future steps */}
              {allStatuses.slice(currentStepIdx + 1).map((s, i) => (
                <div key={s} className="flex gap-3 relative pb-4 opacity-40">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 bg-gray-100">
                    <Clock className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex-1 pt-2">
                    <span className="text-sm text-muted-foreground">{statusLabels[s]}</span>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Delivery & Certs */}
          <TabsContent value="delivery" className="mt-4 space-y-4">
            {/* Linked trucks */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('orders.linkedTrucks')}</div>
              {linkedTrucks.length > 0 ? (
                <div className="space-y-2">
                  {linkedTrucks.map(truck => (
                    <div
                      key={truck.id}
                      className="flex items-center justify-between bg-muted/50 rounded-lg p-3 cursor-pointer hover:bg-muted/70 transition-colors"
                      onClick={() => onNavigateToTruck?.(truck.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-primary" />
                        <div>
                          <div className="text-sm font-medium">{truck.plate}</div>
                          <div className="text-xs text-muted-foreground">{truck.driver?.name} · {truck.material}</div>
                        </div>
                      </div>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground text-center">{t('orders.noTrucks')}</div>
              )}
            </div>

            {/* Certificates */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('orders.certificates')}</div>
              {(order.certificates || []).length > 0 ? (
                <div className="space-y-2">
                  {order.certificates.map((cert, i) => (
                    <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm font-medium">{cert.type}</div>
                          <div className="text-xs text-muted-foreground">{cert.number} · {cert.material}</div>
                        </div>
                      </div>
                      <Badge className={certStatusColors[cert.status]}>{cert.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground text-center">{t('orders.noCertificates')}</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
